<template>
  <section class="faq">
    <div class="section-bg">
      <div class="bg-img"></div>
    </div>
    <div class="container">
      <div class="left">
        <h2>Frequently asked questions</h2>

        <ul class="faq__categories">
          <li
            v-for="category in categories"
            :key="category"
          >
            <button
              type="button"
              class="pill"
              :class="{ active: selectedCategory === category }"
              @click="select(category)"
            >
              {{ category }}
            </button>
          </li>
          <li>
            <button
              type="button"
              class="pill"
              :class="{ active: selectedCategory === 'All' }"
              @click="select('All')"
            >
              All
            </button>
          </li>
        </ul>
      </div>

      <ul class="faq__list">
        <li
          v-for="item in filteredFaqs"
          :key="item.slug"
        >
          <FaqItem :item="item" />
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import FaqItem from './FaqItem/FaqItem.vue';

// FAQ data. Each FAQ can belong to one or multiple categories
const faqs = ref([
  // Most important
  {
    question: 'What does Proxify do exactly?',
    slug: 'what-does-proxify-do-exactly',
    answer:
      'Proxify connects top-tier tech professionals with global companies for long-term, remote roles. ' +
      'We handle matching, contracts, invoicing, and payments so you can focus on delivering results for the client. ' +
      'Proxify isn’t a gig site - it’s a network designed for developers who want to build sustainable careers.',
    categories: ['Most important'],
  },
  {
    question: 'What type of talent are you looking for?',
    slug: 'what-type-of-talent-are-you-looking-for',
    answer:
      'We’re looking for top-tier experienced developers who deliver quality, value clear communication, ' +
      'and want to build long-term careers with international clients. ' +
      'Our network includes specialists across 230+ tech roles from backend, frontend, and fullstack to DevOps, mobile, ' +
      'and data engineering.',
    categories: ['Most important'],
  },
  {
    question: 'Can I work outside Proxify after joining the network?',
    slug: 'can-i-work-outside-proxify-after-joining-the-network',
    answer:
      'Yes, joining the network doesn’t stop you from working with companies outside of Proxify. ' +
      'However, once you begin a full-time engagement with a Proxify client, ' +
      'you’re expected to dedicate your full attention to it and uphold your commitment.',
    categories: ['Most important'],
  },
  {
    question:
      'Do I need to quit my current full-time job when joining the Proxify network?',
    slug: 'do-i-need-to-quit-my-current-full-time-job-when-joining-the-proxify-network',
    answer: `No, you don’t need to quit your current job immediately.<br>If you accept a full-time engagement through Proxify, we’ll work with you and the client to agree on a start date that works for everyone.<br>It’s absolutely fine if you need a bit of time to finish your current work - we respect that.<br>Once you begin your new engagement, though, we expect your full focus and commitment to the Proxify client.`,
    categories: ['Most important'],
  },
  {
    question: 'Can I work from anywhere?',
    slug: 'can-i-work-from-anywhere',
    answer:
      'Yes. Proxify offers fully remote opportunities worldwide. ' +
      'Most clients prefer developers who can overlap within about ±4 hours of their time zone.',
    categories: ['Most important'],
  },
  {
    question: 'Will I get any support once I start working with a client?',
    slug: 'will-i-get-any-support-once-i-start-working-with-a-client',
    answer:
      'Yes. Our team helps you refine your profile, guides you through onboarding, and enables your success during engagements. ' +
      'It’s like having your own personal branding specialist - someone who helps you showcase your strengths and make a great impression with every client.',
    categories: ['Most important'],
  },
  {
    question: 'Are there any fees attached to joining the network?',
    slug: 'are-there-any-fees-attached-to-joining-the-network',
    answer:
      'No. Joining the Proxify network is completely free for developers. ' +
      'You’ll get matched with companies that really want to work with you and gain access to professional growth ' +
      'opportunities to help you keep advancing your career.',
    categories: ['Most important'],
  },
  // Payments
  {
    question: 'How often will I get paid?',
    slug: 'how-often-will-i-get-paid',
    answer:
      'You’re paid once a month through our partner platform Deel. ' +
      'We handle all invoicing and transfers so that you can focus on delivering high-quality work.',
    categories: ['Payments'],
  },
  {
    question: 'Do I need to invoice clients myself?',
    slug: 'do-i-need-to-invoice-clients-myself',
    answer:
      'No. You don’t need to create or send invoices to clients or Proxify. ' +
      'Deel automatically generates your invoices once your time report is approved by Proxify.',
    categories: ['Payments'],
  },
  {
    question: 'What currency will I be paid in?',
    slug: 'what-currency-will-i-be-paid-in',
    answer:
      'Your payment currency depends on your location. Europe and Asia: Payments are made in euros (EUR). ' +
      'Latin America: Payments are made in US dollars (USD).',
    categories: ['Payments'],
  },
  {
    question: 'What payment methods are available?',
    slug: 'what-payment-methods-are-available',
    answer:
      'All payments go through Deel, which supports multiple local and international payout options depending on your setup.',
    categories: ['Payments'],
  },
  {
    question: 'Can I see my payment history?',
    slug: 'can-i-see-my-payment-history',
    answer:
      'Yes, you’ll have access to a clear dashboard showing your payment history, hours, and upcoming payouts.',
    categories: ['Payments'],
  },
  // Contracts
  {
    question: 'Who am I contracting with, the client or your company?',
    slug: 'who-am-i-contracting-with-the-client-or-your-company',
    answer:
      'You’ll sign a consultancy agreement directly with Proxify. ' +
      'We handle the legal, payment, and administrative aspects of your engagements while ' +
      'you work directly with the client team day to day.',
    categories: ['Contracts'],
  },
  {
    question: 'Can I sign the consultancy agreement as a company?',
    slug: 'can-i-sign-the-consultancy-agreement-as-a-company',
    answer:
      'Our consultancy agreement is a B2B contract that can be signed as an individual or a company. ' +
      'Just keep in mind that only you, who applied and was vetted by Proxify, are allowed to perform work under this contract.',
    categories: ['Contracts'],
  },
  {
    question: 'Do I need to sign a new contract for each role?',
    slug: 'do-i-need-to-sign-a-new-contract-for-each-role',
    answer:
      'No. Your work is covered by the main consultancy agreement signed when joining Proxify. ' +
      'You’ll only need to sign an additional Engagement Agreement when you begin working with a new client.',
    categories: ['Contracts'],
  },
  {
    question: 'How long are the roles I can find through Proxify?',
    slug: 'how-long-are-the-roles-i-can-find-through-proxify',
    answer:
      'Most roles are long-term, full-time engagements where you become an integrated member of the client’s team. ' +
      'These engagements are designed to last for months or even years, depending on client goals and your performance.',
    categories: ['Contracts'],
  },
  {
    question: 'Can I end an engagement with a client?',
    slug: 'can-i-end-an-engagement-with-a-client',
    answer:
      'Yes. While it doesn’t happen often, you can end a long-term engagement if needed. ' +
      'Usually, the notice period is 14 days, depending on the type of engagement you are working on.',
    categories: ['Contracts'],
  },
  // Experience
  {
    question: 'What kind of experience do I need to join?',
    slug: 'what-kind-of-experience-do-i-need-to-join',
    answer:
      'You are expected to have at least 3 years of professional experience in your field, ' +
      'strong technical expertise, and excellent communication skills.',
    categories: ['Experience'],
  },
  {
    question: 'Do I need to have worked with international clients before?',
    slug: 'do-i-need-to-have-worked-with-international-clients-before',
    answer:
      'Not necessarily. While it’s a plus, we mainly look for developers who deliver great work, ' +
      'communicate effectively, and are eager to grow in a global environment.',
    categories: ['Experience'],
  },
  {
    question: 'How do you match me with clients?',
    slug: 'how-do-you-match-me-with-clients',
    answer:
      'We don’t just rely on algorithms - we get to know you. Our proprietary matching system and internal team work together to ' +
      'identify opportunities that align with your skills, experience, and goals before bringing you in front of the right client.',
    categories: ['Experience'],
  },
  {
    question: 'Do I need to speak fluent English?',
    slug: 'do-i-need-to-speak-fluent-english',
    answer:
      'Yes. Advanced English is required for successful communication and collaboration with global clients.',
    categories: ['Experience'],
  },
  {
    question: 'What kind of roles can I expect?',
    slug: 'what-kind-of-roles-can-i-expect',
    answer: `Nearly 90% of our roles are full-time, long-term engagements, where you work as an integrated part of a client’s team.
    Part-time or short-term gigs are available occasionally but are less common.`,
    categories: ['Experience'],
  },
  {
    question: 'How long between joining the network and getting my first job?',
    slug: 'how-long-between-joining-the-network-and-getting-my-first-job',
    answer:
      'There’s no fixed timeline - it depends on client demand and finding you the right fit. ' +
      'Sometimes developers are matched within a few weeks, while in other cases it can take a few months. ' +
      'Factors like your time zone, experience, and rate all play a role in how quickly you’ll land your first engagement.',
    categories: ['Experience'],
  },
  // Vetting process
  {
    question: 'What does the vetting process look like?',
    slug: 'what-does-the-vetting-process-look-like',
    answer: `Our vetting includes a technical assessment in your area of expertise (about 90 minutes),
              plus a review of your experience, communication skills, and professionalism.
              <br />Once you pass, you’ll join the Proxify network, and your profile will appear in our proprietary matching system,
              where our team hand-picks developers for suitable client opportunities.`,
    categories: ['Vetting process'],
  },
  {
    question: 'How long does it take to complete the vetting process?',
    slug: 'how-long-does-it-take-to-complete-the-vetting-process',
    answer:
      'Typically, between 3 and 15 days, depending on your availability and how quickly you complete each step.',
    categories: ['Vetting process'],
  },
  {
    question: 'What’s the purpose of the vetting process?',
    slug: 'whats-the-purpose-of-the-vetting-process',
    answer:
      'Our vetting process is designed to highlight your strengths and make sure we connect you with the right opportunities. ' +
      'Once you’re vetted, you won’t need to repeat the process for each new client.',
    categories: ['Vetting process'],
  },
  {
    question: 'Do you verify my identity?',
    slug: 'do-you-verify-my-identity',
    answer:
      'Yes. Your identity or business registration is verified when you set up your Deel account, ensuring compliance and secure payments.',
    categories: ['Vetting process'],
  },
  {
    question: 'How do you decide which clients I’m matched with?',
    slug: 'how-do-you-decide-which-clients-im-matched-with',
    answer:
      'We look at your tech stack, verified skills, experience level, rate, industry background, and time-zone compatibility to ensure every match is a great fit.',
    categories: ['Vetting process'],
  },
]);

const categories = computed(() => {
  return [
    'Most important',
    'Payments',
    'Contracts',
    'Experience',
    'Vetting process',
  ];
});

const selectedCategory = ref('Most important');
const select = (cat) => {
  selectedCategory.value = cat;
};

const filteredFaqs = computed(() => {
  if (selectedCategory.value === 'All') return faqs.value;
  return faqs.value.filter((f) =>
    f.categories.includes(selectedCategory.value)
  );
});
</script>

<style scoped lang="scss">
@import './FaqSection.scss';
</style>
